import React from 'react';
import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components/macro';
import { isEmpty } from 'lodash/fp';
import { useShowSfwSite } from 'src/client/helpers/useFeatureFlags';
import { BlurrableImage } from 'src/client/components/ui/BlurredImage';

type Item = {
  altText: string;
  displayOrder?: number;
  imgSrc: string;
  url: string;
};

type Props = {
  ariaLabel?: string;
  className?: string;
  decoding?: 'async' | 'auto' | 'sync';
  extraProps?: Record<string, string>;
  item: Item;
  loading?: 'eager' | 'lazy';
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  onError?: () => void;
  onLoad?: () => void;
  rel?: string;
  target?: string;
  thumbnailMode?: 'full' | 'square';
};

export const ImageLink = (props: Props) => {
  const {
    ariaLabel,
    className,
    decoding,
    extraProps: state,
    item,
    loading,
    onError,
    onLoad = () => null,
    thumbnailMode,
  } = props;
  const commonProps = { 'aria-label': ariaLabel, className, onClick: props.onClick };
  const basicLink = isEmpty(props.extraProps);
  const isBlurred = useShowSfwSite();
  const LinkedImage = thumbnailMode ? FullThumbnailImage : BlurrableImage;

  const img = (
    <ImageWrapper>
      <LinkedImage
        data-order={item.displayOrder}
        src={item.imgSrc}
        alt={item.altText}
        loading={loading}
        decoding={decoding}
        className="img img-responsive"
        onLoad={onLoad}
        onError={onError}
        ref={(input) => input?.complete && onLoad()}
        $isBlurred={isBlurred}
        $square={thumbnailMode === 'square'}
      />
    </ImageWrapper>
  );

  if (/^https?:/.test(item.url)) {
    return (
      <a
        href={item.url}
        target={props.target ?? '_blank'}
        rel={props.rel ?? 'nofollow noopener noreferrer'}
        {...commonProps}
      >
        {img}
      </a>
    );
  }

  const { hash, pathname, search } = new URL(item.url, location.origin);
  const navLinkLocation = basicLink ? item.url : { hash, pathname, search, state };
  return (
    <NavLink to={navLinkLocation} target={props.target} {...commonProps}>
      {img}
    </NavLink>
  );
};

const ImageWrapper = styled.div`
  position: relative;
`;

const FullThumbnailImage = styled(BlurrableImage).attrs({ 'data-testid': 'gallery-thumbnail-image' })<{
  $square?: boolean;
}>`
  display: block;
  width: 100%;

  ${({ $square }) =>
    $square &&
    css`
      aspect-ratio: 1 / 1;
      object-fit: cover;
    `}
`;
